import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { PageMasterMode, usePageContext } from "../navigation/PageContext";
import { SessionMode } from "../../contexts/appStore/hooks";
import { BaseTopBar } from "./BaseTopBar";
import React, { useCallback, useMemo } from "react";
import { isExpert } from "../../helpers/roles";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useMeetingUtilsContext } from "../../contexts/meetingUtils/context";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";

export interface MasterTopBarProps {
  onSettings?: () => void;
  onBack?: () => void;
  settingsBtnRef?: React.Ref<HTMLDivElement>;
}

export const MasterTopBar: React.FC<MasterTopBarProps> = React.memo(({
  onSettings,
  onBack,
  settingsBtnRef,
}) => {
  const dispatch = useAppStoreDispatch();
  const user = useAppStoreSelector(state => state.user);

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const containerCtx = useMeetingContainerContext({ raiseIfUndefined: false });

  const [meetingId] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.id);
  const [meetingName] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.name);
  const [meetingStartedAt] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.startedAt);
  const [watchingNumber] = useMeetingSelector(containerCtx?.containerAPI, "sessions", sessions => sessions?.filter(s => !isExpert(s)).length);

  const defaultOnBack = useCallback(() => {
    pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
    if (meetingId) {
      dispatch("setLastLeftMeetingId", meetingId);
    }
  }, [dispatch, meetingId]);

  const showDefaultOnBack = useMemo(() => ![PageMasterMode.LIST, PageMasterMode.LOGIN].includes(pageContextRef.current?.page!!), []);

  return (
    <BaseTopBar
      user={user}
      meetingName={meetingName}
      meetingStartedAt={meetingStartedAt}
      showDate={!meetingName} // show date if no meeting
      showLogo={true}
      onSettings={onSettings}
      onBack={onBack ?? (showDefaultOnBack ? defaultOnBack : undefined)}
      watchingNumber={watchingNumber}
      settingsBtnRef={settingsBtnRef}
    />
  );
});